/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useHospitalization(id: string | undefined) {
  return useQuery({
    queryKey: ["hospitalization", id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("hospitalizations")
        .select(`
          id, status, motivo, diagnostico_inicial, observacoes, customer_id,
          entrada_at, previsao_alta_at, alta_at, alta_motivo,
          pets (
            id, name,
            owners ( id, name, owners_phones ( phone_number, is_primary ) )
          ),
          hospitalization_bays (
            id, nome,
            hospitalization_sectors ( id, nome )
          ),
          hospitalization_evolutions ( id, registrado_at, peso_kg, temperatura_c, fc_bpm, fr_rpm, tpc_s, condicao_geral, observacoes ),
          hospitalization_prescriptions (
            id, medicamento, dose, via, frequencia_h, inicio_at, fim_at, ativa, observacoes,
            hospitalization_doses ( id, administrado_at )
          ),
          hospitalization_diet ( id, registrado_at, tipo, descricao, quantidade_ml ),
          hospitalization_exams ( id, solicitado_at, tipo, descricao, resultado, resultado_at, status ),
          hospitalization_contatos_tutor ( id, registrado_at, tipo, resumo, autorizacao ),
          hospitalization_finance ( id, registrado_at, descricao, tipo, quantidade, valor_unitario, valor_total )
        `)
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as any;
    },
    enabled: !!id,
  });
}

export function useRegistrarAlta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, motivo }: { id: string; motivo: string }) => {
      const { error } = await (supabase as any)
        .from("hospitalizations")
        .update({ status: "alta", alta_at: new Date().toISOString(), alta_motivo: motivo })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", id] });
      queryClient.invalidateQueries({ queryKey: ["hospitalizations"] });
      queryClient.invalidateQueries({ queryKey: ["hospitalization-setores"] });
    },
  });
}

export function useAddEvolucao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hospitalization_id: string;
      customer_id: string;
      peso_kg?: number | null;
      temperatura_c?: number | null;
      fc_bpm?: number | null;
      fr_rpm?: number | null;
      tpc_s?: number | null;
      condicao_geral?: number | null;
      observacoes?: string | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_evolutions")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}

export function useAddPrescricao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hospitalization_id: string;
      customer_id: string;
      medicamento: string;
      dose: string;
      via: string;
      frequencia_h: number;
      inicio_at?: string;
      observacoes?: string | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_prescriptions")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}

export function useRegistrarDose() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      prescricao_id: string;
      hospitalization_id: string;
      customer_id: string;
      observacoes?: string | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_doses")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}

export function useAddDieta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hospitalization_id: string;
      customer_id: string;
      tipo: string;
      descricao: string;
      quantidade_ml?: number | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_diet")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}

export function useAddExame() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hospitalization_id: string;
      customer_id: string;
      tipo: string;
      descricao: string;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_exams")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}

export function useAddContatoTutor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hospitalization_id: string;
      customer_id: string;
      tipo: string;
      resumo: string;
      autorizacao?: string | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_contatos_tutor")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}

export function useAddFinanceiro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      hospitalization_id: string;
      customer_id: string;
      descricao: string;
      tipo: string;
      quantidade: number;
      valor_unitario: number;
    }) => {
      const { data, error } = await (supabase as any)
        .from("hospitalization_finance")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization", input.hospitalization_id] });
    },
  });
}
